'use client';

import { ActionBarPrimitive, useAuiState } from '@assistant-ui/react';
import { AudioLines, CircleStop, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { TTS_DOUBLE_TAP_MS } from '@/common/constants';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { useClickClaim } from '@/hooks/use-click-claim';
import { stopActiveTts } from '@/lib/tts/active-tts';

// `message.speech` is tagged `@deprecated` to mark it experimental; there is no
// replacement to migrate to.
function useMessageSpeechStatusType() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return useAuiState(({ message }) => message.speech?.status.type ?? null);
}

export function useIsMessageSpeaking() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return useAuiState(({ message }) => message.speech != null);
}

export function TtsSpeechButton() {
  const t = useTranslations();
  const speechStatusType = useMessageSpeechStatusType();
  const { tryClaim: tryClaimSpeak, reset: resetSpeakWindow } = useClickClaim(TTS_DOUBLE_TAP_MS);

  if (speechStatusType === 'running') {
    return (
      <TooltipIconButton
        tooltip={t('thread.tooltips.stopAudio')}
        side="bottom"
        className="aui-button-icon size-6 p-1"
        // Not ActionBarPrimitive.StopSpeaking: its runtime call throws once the
        // utterance has ended. stopActiveTts is idempotent.
        onClick={() => {
          resetSpeakWindow(); // an explicit stop makes the next play deliberate
          stopActiveTts();
        }}
      >
        <CircleStop className="size-3.5" />
      </TooltipIconButton>
    );
  }

  if (speechStatusType === 'starting') {
    return (
      <TooltipIconButton
        tooltip={t('thread.tooltips.loadingAudio')}
        side="bottom"
        disabled
        className="aui-button-icon size-6 p-1"
      >
        <Loader2 className="size-3.5 animate-spin" />
      </TooltipIconButton>
    );
  }

  return (
    <ActionBarPrimitive.Speak asChild>
      <TooltipIconButton
        tooltip={t('thread.tooltips.playAudio')}
        side="bottom"
        className="aui-button-icon size-6 p-1"
        // preventDefault is how Radix's composeEventHandlers cancels speak().
        onClick={e => {
          if (!tryClaimSpeak()) e.preventDefault();
        }}
      >
        <AudioLines className="size-3.5" />
      </TooltipIconButton>
    </ActionBarPrimitive.Speak>
  );
}
